import { useState, useEffect, type ChangeEvent } from 'react';
import * as XLSX from 'xlsx';
import {
  RotateCcw,
  Building2,
  Clock,
  AlertTriangle,
  Zap,
  FileSpreadsheet,
  Loader2,
  CheckCircle2,
  Users,
  LogOut,
  Wallet,
  RefreshCw,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { usePayroll } from '@/context/AppContext';
import { useToast } from '@/hooks/useToast';
import { buildPayroll, processAttendance, processSalary, applyPayrollFilters } from '@/lib/payroll';

const DEFAULT_THRESHOLDS = { free_until: 15, tier1: 15, tier2: 30, tier3: 60 };
const DEFAULT_MISS = { miss_in: 0.5, miss_out: 0.5 };

// ✅ إلغاء زيادة/نقصان القيم بعجلة الماوس في خانات الأرقام
const blockWheel = (e: React.WheelEvent<HTMLInputElement>) => {
  e.currentTarget.blur();
};

export function SettingsPage() {
  const {
    companyName,
    setCompanyName,
    thresholds,
    setThresholds,
    earlyThresholds,
    setEarlyThresholds,
    missDeductions,
    setMissDeductions,
    overtimeMode,
    setOvertimeMode,
    skipRows,
    setSkipRows,
    excludeLastDayMissPenalty,
    setExcludeLastDayMissPenalty,
    excludeEmployeesWithoutAttendance,
    setExcludeEmployeesWithoutAttendance,
    excludeEmployeesWithZeroSalary,
    setExcludeEmployeesWithZeroSalary,
    attendanceFile,
    salaryFile,
    adminAdj,
    setPayrollData,
    setPeriod,
    setRawById,
  } = usePayroll();
  const { show } = useToast();
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  // simple indicator — saving on every change (debounced in context)
  useEffect(() => {
    setSaving(true);
    const t = setTimeout(() => setSaving(false), 1500);
    return () => clearTimeout(t);
  }, [
    thresholds,
    earlyThresholds,
    missDeductions,
    overtimeMode,
    companyName,
    excludeLastDayMissPenalty,
    excludeEmployeesWithoutAttendance,
    excludeEmployeesWithZeroSalary,
  ]);

  const updThreshold = (k: keyof typeof thresholds, v: number) =>
    setThresholds((p: any) => ({ ...p, [k]: v }));
  const updEarly = (k: keyof typeof earlyThresholds, v: number) =>
    setEarlyThresholds((p: any) => ({ ...p, [k]: v }));

  const onNum = (cb: (n: number) => void) => (e: ChangeEvent<HTMLInputElement>) =>
    cb(parseInt(e.target.value) || 0);
  const onFloat = (cb: (n: number) => void) => (e: ChangeEvent<HTMLInputElement>) =>
    cb(parseFloat(e.target.value) || 0);

  // ✅ إعادة احتساب المرتبات بالقواعد الجديدة (الإعدادات بتتحفظ تلقائي)
  const recalculate = async () => {
    if (!attendanceFile || !salaryFile) {
      show('ارفع ملف البصمة وملف الموظفين من صفحة الرفع قبل ما تعمل إعادة حساب', 'warning');
      return;
    }
    setRecalculating(true);
    try {
      const [attBuf, salBuf] = await Promise.all([
        attendanceFile.arrayBuffer(),
        salaryFile.arrayBuffer(),
      ]);
      const attWb = XLSX.read(attBuf, { type: 'array' });
      const salWb = XLSX.read(salBuf, { type: 'array' });
      const { records, period, rawById } = processAttendance(
        attWb,
        skipRows,
        thresholds,
        earlyThresholds,
        overtimeMode,
        missDeductions,
        excludeLastDayMissPenalty,
        excludeEmployeesWithoutAttendance,
      );
      const salaryRows = processSalary(salWb);
      const built = buildPayroll(records, salaryRows, adminAdj || {});
      // ✅ طبّق فلتر "مرتبه الإجمالي = 0"
      const filtered = applyPayrollFilters(built, {
        excludeEmployeesWithoutAttendance,
        excludeEmployeesWithZeroSalary,
      });
      setPayrollData(filtered);
      setPeriod(period);
      setRawById(rawById);
      show(`تم إعادة احتساب ${filtered.length} موظف بالقواعد الجديدة`);
    } catch (e: any) {
      show('خطأ: ' + e.message, 'error');
      console.error(e);
    } finally {
      setRecalculating(false);
    }
  };

  const hasFiles = !!attendanceFile && !!salaryFile;

  return (
    <div className="space-y-5 animate-slide-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-extrabold flex items-center gap-2">
          <span>⚙️</span> إعدادات الجزاءات والمدد
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          {saving ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800/50 font-semibold">
              <Loader2 size={14} className="animate-spin" /> جاري الحفظ على السحابة...
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/50 font-semibold">
              <CheckCircle2 size={14} /> محفوظ تلقائياً على حسابك
            </span>
          )}
          <Button
            onClick={recalculate}
            loading={recalculating}
            disabled={recalculating || !hasFiles}
            size="sm"
            variant="primary"
            leftIcon={!recalculating && <RefreshCw size={14} />}
            title={!hasFiles ? 'ارفع الملفات من صفحة الرفع أولاً' : 'إعادة احتساب المرتبات بالقواعد الجديدة'}
          >
            {recalculating ? 'جاري الحساب...' : 'إعادة الحساب'}
          </Button>
        </div>
      </div>

      <Card accent="indigo" padding="sm">
        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed flex items-start gap-2">
          <AlertTriangle size={14} className="text-indigo-500 shrink-0 mt-0.5" />
          <span>
            الإعدادات بتتحفظ تلقائياً على Firebase على حسابك. بعد أي تعديل، اضغط{' '}
            <strong>"إعادة الحساب"</strong> بالأعلى عشان تطبّق القواعد الجديدة على المرتبات
            الحالية (مش محتاج تعيد رفع الملفات).
          </span>
        </p>
      </Card>

      {/* Company name */}
      <Card accent="emerald" padding="lg">
        <h3 className="font-bold mb-4 flex items-center gap-2 text-slate-700 dark:text-slate-200">
          <Building2 size={18} /> اسم الشركة / المؤسسة
        </h3>
        <p className="text-xs text-slate-500 mb-4">يظهر الاسم ده في التقارير المطبوعة (الظروف، A4، وكشف المرتبات)</p>
        <Input
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          placeholder="مثلاً: شركة الأمل للصناعات الغذائية"
          maxLength={80}
        />
      </Card>

      {/* Late thresholds */}
      <Card accent="indigo" padding="lg">
        <h3 className="font-bold mb-4 flex items-center gap-2 text-slate-700 dark:text-slate-200">
          <Clock size={18} /> عتبات خصم التأخير (بالدقائق بعد ميعاد الدخول)
        </h3>
        <p className="text-xs text-slate-500 mb-4">يتم تطبيق هذه العتبات بشكل نسبي على ميعاد الدخول الافتراضي لكل موظف</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { key: 'free_until', label: 'حد مجاني (لا خصم)', def: 15, suffix: ' دقيقة' },
            { key: 'tier1', label: 'حتى 0.125 يوم', def: 15, suffix: ' دقيقة' },
            { key: 'tier2', label: 'حتى 0.25 يوم', def: 30, suffix: ' دقيقة' },
            { key: 'tier3', label: 'حتى 0.5 يوم', def: 60, suffix: ' دقيقة' },
          ].map((t) => (
            <div key={t.key} className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
              <label className="text-xs text-slate-500 dark:text-slate-400 block mb-2 font-semibold">
                {t.label}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="999"
                  dir="ltr"
                  onWheel={blockWheel}
                  value={(thresholds as any)[t.key]}
                  onChange={onNum((v) => updThreshold(t.key as any, v))}
                  className="form-input w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-center font-bold text-indigo-600"
                />
                <span className="text-xs text-slate-500 shrink-0">{t.suffix}</span>
              </div>
            </div>
          ))}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="mt-3 text-indigo-600 hover:text-indigo-800"
          onClick={() => setThresholds({ ...DEFAULT_THRESHOLDS })}
        >
          <RotateCcw size={14} /> استعادة الافتراضي
        </Button>
      </Card>

      {/* Early thresholds */}
      <Card accent="amber" padding="lg">
        <h3 className="font-bold mb-4 flex items-center gap-2 text-slate-700 dark:text-slate-200">
          <AlertTriangle size={18} /> عتبات خصم الخروج المبكر (بالدقائق قبل ميعاد الخروج)
        </h3>
        <p className="text-xs text-slate-500 mb-4">يتم تطبيق هذه العتبات بشكل نسبي على ميعاد الخروج الافتراضي لكل موظف</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { key: 'free_until', label: 'حد مجاني (لا خصم)', def: 15, suffix: ' دقيقة' },
            { key: 'tier1', label: 'حتى 0.125 يوم', def: 15, suffix: ' دقيقة' },
            { key: 'tier2', label: 'حتى 0.25 يوم', def: 30, suffix: ' دقيقة' },
            { key: 'tier3', label: 'حتى 0.5 يوم', def: 60, suffix: ' دقيقة' },
          ].map((t) => (
            <div key={t.key} className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
              <label className="text-xs text-slate-500 dark:text-slate-400 block mb-2 font-semibold">
                {t.label}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="999"
                  dir="ltr"
                  onWheel={blockWheel}
                  value={(earlyThresholds as any)[t.key]}
                  onChange={onNum((v) => updEarly(t.key as any, v))}
                  className="form-input w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-center font-bold text-amber-600"
                />
                <span className="text-xs text-slate-500 shrink-0">{t.suffix}</span>
              </div>
            </div>
          ))}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="mt-3 text-amber-600 hover:text-amber-800"
          onClick={() => setEarlyThresholds({ ...DEFAULT_THRESHOLDS })}
        >
          <RotateCcw size={14} /> استعادة الافتراضي
        </Button>
      </Card>

      {/* Miss punch */}
      <Card accent="sky" padding="lg">
        <h3 className="font-bold mb-4 flex items-center gap-2 text-slate-700 dark:text-slate-200">
          <AlertTriangle size={18} /> عقوبات نسيان البصمة (بالأيام)
        </h3>
        <p className="text-xs text-slate-500 mb-4">يتم تطبيق هذه العقوبات عند نسيان الموظف تسجيل بصمة الدخول أو الخروج</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
            <label className="text-xs text-slate-500 dark:text-slate-400 block mb-2 font-semibold">
              نسيان بصمة الدخول
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.125"
                min="0"
                max="2"
                dir="ltr"
                onWheel={blockWheel}
                value={missDeductions.miss_in}
                onChange={onFloat((v) => setMissDeductions((p: any) => ({ ...p, miss_in: v })))}
                className="form-input w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-center font-bold text-sky-600"
              />
              <span className="text-xs text-slate-500 shrink-0">يوم</span>
            </div>
          </div>
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
            <label className="text-xs text-slate-500 dark:text-slate-400 block mb-2 font-semibold">
              نسيان بصمة الخروج
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.125"
                min="0"
                max="2"
                dir="ltr"
                onWheel={blockWheel}
                value={missDeductions.miss_out}
                onChange={onFloat((v) => setMissDeductions((p: any) => ({ ...p, miss_out: v })))}
                className="form-input w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-center font-bold text-sky-600"
              />
              <span className="text-xs text-slate-500 shrink-0">يوم</span>
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="mt-3 text-sky-600 hover:text-sky-800"
          onClick={() => setMissDeductions({ ...DEFAULT_MISS })}
        >
          <RotateCcw size={14} /> استعادة الافتراضي
        </Button>
      </Card>

      {/* OT mode */}
      <Card accent="sky" padding="lg">
        <h3 className="font-bold mb-4 flex items-center gap-2 text-slate-700 dark:text-slate-200">
          <Zap size={18} /> وضع تقريب ساعات الإضافي
        </h3>
        <p className="text-xs text-slate-500 mb-4">اختر كيفية احتساب الإضافي بعد قراءته من عمود Overtime Duration</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <label
            className={`relative flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
              overtimeMode === 'rounded'
                ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20 shadow-md'
                : 'border-slate-200 dark:border-slate-700 hover:border-sky-300'
            }`}
          >
            <input
              type="radio"
              name="otmode"
              value="rounded"
              checked={overtimeMode === 'rounded'}
              onChange={() => setOvertimeMode('rounded')}
              className="mt-1"
            />
            <div>
              <div className="font-bold text-slate-800 dark:text-white">⏱️ تقريب لـ 2/4/6/8 ساعة</div>
              <div className="text-xs text-slate-500 mt-1">
                كل يوم يُقرب الإضافي فيه لأقرب قيمة من &#123;2, 4, 6, 8&#125; ساعة. مثال: 1:08 → 2 ساعة، 3:45 → 4 ساعة
              </div>
            </div>
          </label>
          <label
            className={`relative flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
              overtimeMode === 'hourly'
                ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20 shadow-md'
                : 'border-slate-200 dark:border-slate-700 hover:border-sky-300'
            }`}
          >
            <input
              type="radio"
              name="otmode"
              value="hourly"
              checked={overtimeMode === 'hourly'}
              onChange={() => setOvertimeMode('hourly')}
              className="mt-1"
            />
            <div>
              <div className="font-bold text-slate-800 dark:text-white">🕐 تقريب لأقرب ساعة</div>
              <div className="text-xs text-slate-500 mt-1">
                كل يوم يُقرب الإضافي فيه لأقرب ساعة كاملة. مثال: 1:08 → 1 ساعة، 3:45 → 4 ساعة
              </div>
            </div>
          </label>
        </div>
      </Card>

      {/* Skip rows */}
      <Card padding="lg">
        <h3 className="font-bold mb-4 flex items-center gap-2 text-slate-700 dark:text-slate-200">
          <FileSpreadsheet size={18} /> إعدادات قراءة الملف
        </h3>
        <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">
          عدد صفوف التخطي في ملف البصمة
        </label>
        <input
          type="number"
          min="0"
          max="10"
          dir="ltr"
          onWheel={blockWheel}
          value={skipRows}
          onChange={onNum(setSkipRows)}
          className="form-input w-32 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-center"
        />
        <p className="text-xs text-slate-500 mt-2">
          افتراضياً 3 (لأن الملف فيه 3 صفوف header: اسم الشركة، اسم التقرير، وقت التصدير)
        </p>
      </Card>

      {/* ✅ خيارات الحساب (جديد) */}
      <Card accent="violet" padding="lg">
        <h3 className="font-bold mb-4 flex items-center gap-2 text-slate-700 dark:text-slate-200">
          <Zap size={18} /> خيارات الحساب
        </h3>
        <div className="space-y-3">
          {/* ✅ إلغاء عقوبات نسيان الخروج في آخر يوم */}
          <label className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/30 cursor-pointer transition-colors">
            <input
              type="checkbox"
              checked={excludeLastDayMissPenalty}
              onChange={(e) => setExcludeLastDayMissPenalty(e.target.checked)}
              className="mt-1 w-4 h-4 accent-violet-500"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 font-bold text-slate-800 dark:text-slate-100 text-sm">
                <LogOut size={14} className="text-orange-500" />
                عدم احتساب عقوبات نسيان الخروج لآخر يوم في الفترة
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                لو الموظف نسي يبصم خروج في آخر يوم في الفترة، العقوبة (جزاء النسيان) مش هتتطبق
                — بيشيل خصم النسيان بس ليوم نهاية الفترة.
              </div>
            </div>
          </label>

          {/* ✅ إهمال الموظفين اللي مفيش ليهم حضور فعلي */}
          <label className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/30 cursor-pointer transition-colors">
            <input
              type="checkbox"
              checked={excludeEmployeesWithoutAttendance}
              onChange={(e) => setExcludeEmployeesWithoutAttendance(e.target.checked)}
              className="mt-1 w-4 h-4 accent-violet-500"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 font-bold text-slate-800 dark:text-slate-100 text-sm">
                <Users size={14} className="text-rose-500" />
                إهمال الموظفين الذين ليس لهم حضور في الفترة
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                الموظف اللي عدد أيام حضوره الفعلية = 0 في الفترة (كلها غياب أو مفيش بصمات) مش
                هيظهر في التقرير — بيقلل الفوضى في النتائج.
              </div>
            </div>
          </label>

          {/* ✅ إهمال الموظف اللي مرتبه الإجمالي = 0 */}
          <label className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/30 cursor-pointer transition-colors">
            <input
              type="checkbox"
              checked={excludeEmployeesWithZeroSalary}
              onChange={(e) => setExcludeEmployeesWithZeroSalary(e.target.checked)}
              className="mt-1 w-4 h-4 accent-violet-500"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 font-bold text-slate-800 dark:text-slate-100 text-sm">
                <Wallet size={14} className="text-amber-500" />
                إهمال أي موظف قيمة مرتبه الإجمالية صفر
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                لو الموظف طلّع صافي = 0 ج.م (بعد كل الخصومات وقبل التعديل الإداري) مش هيظهر في
                التقرير — بيشيل الموظفين اللي البيانات بتاعتهم ناقصة (يومية = 0 أو كل يوم جزاء).
              </div>
            </div>
          </label>
        </div>
      </Card>
    </div>
  );
}
